// What an epic-stop actually did, in one sentence (#1906) — pure, so the three
// outcomes are provable without rendering a route.
//
// `POST /v1/plans/:planId/cancel` answers THREE lists and they are independent:
// one response routinely carries both cancelled and already-terminal slices, and a
// response with skipped reviews still cancelled everything else. Reporting only
// the first non-empty one — which an early return does — hides outcomes that
// happened. The skipped list is the one that matters most: those slices are STILL
// RUNNING, and an operator who pressed stop is owed that rather than a quieter
// success.

import { t, tPlural } from '../i18n';
import { joinSentences } from './join-sentences';
import type { PlanCancelResult } from './plan';

/**
 * One line naming every outcome the server reported, in the order an operator
 * cares about: what stopped, what was already done, what KEPT RUNNING and why.
 *
 * `skipped_reason` is the server's own sentence for the review carve-out and is
 * appended verbatim when it sent one — the same rule the task refusal follows.
 * A response where nothing moved says so explicitly rather than rendering empty.
 */
export function planStopMessage(result: PlanCancelResult): string {
  // (#4037) A plan that already landed (`done`, #4018) renders its own line
  // FIRST — distinct from the `nothing to stop` lie a `cancelled` re-cancel
  // prints. The cancel CAS on the plan row is `status = 'open'`, so a
  // `done` plan keeps `done` here and the wire carries it forward.
  if (result.plan_status === 'done') {
    return t('fleet.stop.plan_already_landed');
  }
  const parts: string[] = [];
  // (#2696) Plan-group cancellation lands on its OWN line — distinct from
  // task cancellation, so a fully-held plan reads as
  // "Cancelled 3 held slices — nothing was dispatched yet" rather than
  // "Nothing to stop, every slice had already finished" (`fleet.stop.plan_none`).
  // `cancelled_group_count` is absent on responses predating #2696; treat as 0.
  const cancelledGroups = result.cancelled_group_count ?? 0;
  if (cancelledGroups > 0) {
    parts.push(
      tPlural('fleet.stop.plan_cancelled', cancelledGroups, { count: String(cancelledGroups) }),
    );
  }
  if (result.cancelled_task_ids.length > 0) {
    const n = result.cancelled_task_ids.length;
    parts.push(tPlural('fleet.stop.plan_stopped', n, { count: String(n) }));
  }
  if (result.already_terminal_task_ids.length > 0) {
    const n = result.already_terminal_task_ids.length;
    parts.push(tPlural('fleet.stop.plan_already', n, { count: String(n) }));
  }
  if (result.skipped_review_task_ids.length > 0) {
    const n = result.skipped_review_task_ids.length;
    parts.push(tPlural('fleet.stop.plan_skipped', n, { count: String(n) }));
    if (result.skipped_reason) parts.push(result.skipped_reason);
  }
  return parts.length > 0 ? joinSentences(parts) : t('fleet.stop.plan_none');
}
